// Libs
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
// Utils
import GENERAL from 'utils/constants/general';
import { GlobalUtils } from 'utils';
import {
	getZoneIdFromOption,
	requestZoneAssignment,
} from './useOrderZoneAssignment.utils';
// Actions
import { sendToast } from 'screens/Dashboard/actions';
import { updateDatasourceItem } from 'core/actions';
// Selectors
import { selectDatasourceData } from 'core/selectors';
// Hooks
import useOrderApi from './useOrderApi';

const { UNSUCCESS } = GENERAL.ENV.STATUS;

const useOrderZoneAssignment = () => {
	const dispatch = useDispatch();
	const { getZoneAssignment, setZoneAssignment } = useOrderApi();
	const datasource = useSelector(selectDatasourceData);
	const isMountedRef = useRef(true);
	const getRequestIdRef = useRef(0);
	const lastSearchValueRef = useRef(undefined);

	const [state, setState] = useState({
		isLoading: false,
		isSetting: false,
		data: [],
		status: undefined,
		message: undefined,
	});

	useEffect(
		() => () => {
			isMountedRef.current = false;
			getRequestIdRef.current += 1;
		},
		[],
	);

	const onGetZoneAssignment = async ({ searchValue } = {}) => {
		if (!isMountedRef.current) return;

		lastSearchValueRef.current = searchValue;
		const requestId = ++getRequestIdRef.current;
		setState(prev => ({
			...prev,
			isLoading: true,
			data: [],
			status: undefined,
			message: undefined,
		}));

		try {
			const response = await getZoneAssignment({ searchValue });
			if (!isMountedRef.current || requestId !== getRequestIdRef.current)
				return;

			setState(prev => ({
				...prev,
				data: GlobalUtils.checkArray(response),
			}));
		} catch (err) {
			if (!isMountedRef.current || requestId !== getRequestIdRef.current)
				return;

			setState(prev => ({
				...prev,
				data: [],
				status: UNSUCCESS,
				message: err.message || '',
			}));
			dispatch(sendToast({ message: err.message, type: err.type }));
		} finally {
			if (isMountedRef.current && requestId === getRequestIdRef.current)
				setState(prev => ({ ...prev, isLoading: false }));
		}
	};

	const onRetry = () =>
		onGetZoneAssignment({ searchValue: lastSearchValueRef.current });

	const onSetZoneAssignment = async ({ option, orderId }, field) => {
		const zoneId = getZoneIdFromOption(option);

		if (!zoneId) {
			const message = 'No se pudo identificar la zona seleccionada';

			dispatch(sendToast({ message, type: 'error' }));
			setState(prev => ({
				...prev,
				isSetting: false,
				status: UNSUCCESS,
				message,
			}));
			return;
		}

		setState(prev => ({ ...prev, isSetting: true }));

		try {
			const response = await requestZoneAssignment({
				option,
				orderId,
				setZoneAssignment,
			});

			if (response.status === UNSUCCESS) {
				setState(prev => ({
					...prev,
					isSetting: false,
					status: response.status,
					message: response.message,
				}));
				return;
			}

			dispatch(
				updateDatasourceItem({
					updateRecords: [
						{
							id: response.data.orderId,
							fields: {
								[field.id]: Number(response.data.zoneId),
								[field.name]: response.data.zoneName,
								townshipName: response.data.townshipName,
								cityName: response.data.cityName,
							},
						},
					],
				}),
			);
			field?.setReload?.(true);
			setState(prev => ({ ...prev, isSetting: false }));
		} catch (err) {
			dispatch(sendToast({ message: err.message, type: err.type }));
			setState(prev => ({
				...prev,
				isSetting: false,
				status: UNSUCCESS,
				message: err.message || '',
			}));

			throw err;
		}
	};

	return { state, onGetZoneAssignment, onSetZoneAssignment, onRetry };
};

export default useOrderZoneAssignment;
